import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PjSandboxBoardKey,
  PjSandboxDocumentKey,
  PjSandboxBoardResponse,
  PutPjSandboxBoardRequest,
} from '@cortex/domain-product-innovation-lab-types';
import { pjSandboxApi } from '../lib/api.js';

/** 白板本体の query key（保存後の setQueryData で共有）。@graph-connects none */
export function pjSandboxBoardKeyOf(boardKey: PjSandboxDocumentKey): readonly unknown[] {
  return ['pj-sandbox', 'board', boardKey];
}

/**
 * 編集履歴の query key（保存後の invalidate で共有）。共有未配置トレイ (`shared-tray`) の
 * 保存でも呼ばれるため、チーム白板より広い `PjSandboxDocumentKey` を受ける。
 * @graph-connects none
 */
export function pjSandboxHistoryKeyOf(boardKey: PjSandboxDocumentKey): readonly unknown[] {
  return ['pj-sandbox', 'history', boardKey];
}

/**
 * PJ Sandbox 白板（チーム共有の保存状態）の取得フック。
 *
 * 白板はチーム全員で 1 枚を共有し、他の編集者の変更を取りこぼさないようにしたいので
 * `staleTime: 0` にして、再フォーカス・再マウント時に取り直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の共有状態取得フック（ブロック・候補案件・リビジョン・編集権限）
 * @graph-connects /pj-sandbox/boards [calls] 白板の共有状態取得 API
 */
export function usePjSandboxBoard(boardKey: PjSandboxDocumentKey) {
  return useQuery({
    queryKey: pjSandboxBoardKeyOf(boardKey),
    queryFn: () => pjSandboxApi.getBoard(boardKey),
    staleTime: 0,
  });
}

/**
 * PJ Sandbox 白板の保存（全置換 + 編集履歴の追記）ミューテーション。
 *
 * 成功時は取得 query を保存後の状態で差し替え（リビジョンを進めるため）、
 * 編集履歴は invalidate して取り直す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 白板の保存フック（全置換保存 + 操作履歴の追記）
 * @graph-connects /pj-sandbox/boards [calls] 白板の保存 API (PUT)
 */
export function useSavePjSandboxBoard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { boardKey: PjSandboxDocumentKey; body: PutPjSandboxBoardRequest }) =>
      pjSandboxApi.saveBoard(input.boardKey, input.body),
    onSuccess: (data: PjSandboxBoardResponse, variables) => {
      queryClient.setQueryData(pjSandboxBoardKeyOf(variables.boardKey), data);
      void queryClient.invalidateQueries({
        queryKey: pjSandboxHistoryKeyOf(variables.boardKey),
      });
    },
  });
}

/**
 * PJ Sandbox 白板の編集履歴（新しい順）の取得フック。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PJ Sandbox 編集履歴の取得フック（誰がどの操作をしたか、新しい順）
 * @graph-connects /pj-sandbox/boards/history [calls] 白板の編集履歴取得 API
 */
export function usePjSandboxHistory(
  boardKey: PjSandboxBoardKey,
  options: { limit: number; enabled: boolean },
) {
  return useQuery({
    queryKey: [...pjSandboxHistoryKeyOf(boardKey), options.limit],
    queryFn: () => pjSandboxApi.getHistory(boardKey, { limit: options.limit }),
    enabled: options.enabled,
    staleTime: 0,
  });
}
